import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import appStoreBanner from 'assets/svg/app-store.svg';
import closeIcon from 'assets/svg/close-white.svg';
import getAppOverlay from 'assets/svg/getapp-overlay.svg';
import getAppOverlayMobile from 'assets/svg/getapp-overlay-mobile.svg';
import googlePlayBanner from 'assets/svg/google-play.svg';
import { H1, H5, H6 } from 'components/typography/Headers';
import QRCodeComponent from 'components/ui-elements/QRCodeComponent';
import StoreBanner from 'components/ui-elements/StoreBanner';
import { DEFAULT_APP_AREA, PublicDeepLinkAppAreaType } from 'services/notification/notificationService.dto';
import { appStoreLink, googlePlayLink, isIOSDevice } from 'utils/mobileStoreRedirections';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80vw;
  max-width: 720px;
  max-height: 80vh;

  ${theme.mq.phone} {
    width: 100%;
    max-height: 100vh;
  }
`;

const Overlay = styled.img`
  position: absolute;
  width: 100%;
  z-index: -1;

  display: block;
  ${theme.mq.phone} {
    display: none;
  }
`;

const OverlayMobile = styled(Overlay)`
  display: none;
  ${theme.mq.phone} {
    display: block;
  }
`;

const CloseButton = styled.img`
  position: absolute;
  top: 23px;
  right: 24px;
  cursor: pointer;
`;

const Header = styled(H5)`
  margin-top: 25px;
  color: ${theme.color.white};
`;

const Description = styled(H1)`
  color: ${theme.color.white};
  margin-top: 18px;
  white-space: pre-line;
`;

const Info = styled(H6)`
  color: ${theme.color.white};
  margin-top: 36px;
  font-size: 20px;
`;

const InfoBold = styled(H5)`
  color: ${theme.color.white};
  font-weight: 800;
`;

const StoreBannerContainer = styled.div`
  margin-top: 39px;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  ${theme.mq.phone} {
    flex-direction: column;
  }
`;

interface Props {
  appAreaType?: PublicDeepLinkAppAreaType;
  objectId?: number;
  header?: string;
  description?: string;
  onClose?: () => void;
}

export const GetApp = (props: Props) => {
  const { t } = useTranslation();
  const { description, header, appAreaType, objectId, onClose } = props;
  const context = appAreaType || DEFAULT_APP_AREA;

  const canDisplayGoogleBanner = () => {
    return !isMobile || !isIOSDevice();
  };

  const canDisplayAppleBanner = () => {
    return !isMobile || isIOSDevice();
  };

  return (
    <Container data-testid={'get-app-popup'}>
      <Overlay src={getAppOverlay} />
      <OverlayMobile src={getAppOverlayMobile} />
      <CloseButton data-testid={'close-button'} src={closeIcon} onClick={onClose} />
      <Header data-testid={'get-app-header'}>{header || t('get-app.header')}</Header>
      <Description data-testid={'get-app-description'}>{description || t(`${context}.description`)}</Description>
      <>
        <Info>{t('get-app.call-to-action')}</Info>
        <InfoBold>{t('get-app.download-app')}</InfoBold>
      </>
      <StoreBannerContainer>
        {canDisplayAppleBanner() && (
          <StoreBanner link={appStoreLink()} imgPath={appStoreBanner} alt={t('navbar.apple')} />
        )}
        {canDisplayGoogleBanner() && (
          <StoreBanner link={googlePlayLink()} imgPath={googlePlayBanner} alt={t('navbar.google')} />
        )}
      </StoreBannerContainer>
      <QRCodeComponent context={context} objectId={objectId} variant={'SMALL'} />
    </Container>
  );
};
